import { normalizeFileName } from "../../../utils/file";
import { type ManagedFile, type NewUserFile, type UserFile, downloadFile } from "../../../utils/web-api";
import { toastError } from "../../../utils/toast";
import { useAnalytics } from "../../../context/AnalyticsContext";
import { useDioptraFetch } from "../../../hooks/use-dioptra-fetch";
import React, { useRef, useState } from "react";
import type { AttachmentFile } from "../attachment-file";
import type { ErrorContext } from "src/context/AnalyticsContext";

export interface UseLocalFilesPanelSavedFilesInputs {
  createFile: (data: NewUserFile) => Promise<UserFile | null>;
  sendError: (error: unknown, context?: ErrorContext | Record<string, any>) => void;
  canManageFiles: boolean;
  deleteFile: (id: string, isManaged: boolean) => Promise<void>;
  onSelectSavedFile: (fileName: string, fileUrl: string) => void;
  selectedFiles: (File | AttachmentFile)[];
  onRemoveSavedFile: (fileName: string) => void;
}

export function useLocalFilesPanelSavedFiles({ createFile, sendError, canManageFiles, deleteFile, onSelectSavedFile, selectedFiles, onRemoveSavedFile }: UseLocalFilesPanelSavedFilesInputs) {
  const { dioptraFetch } = useDioptraFetch();
  const { analytics } = useAnalytics();
  const addFileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<{ file: UserFile | ManagedFile; managed: boolean } | null>(null);
  const handleAddFile = () => {
    if (addFileInputRef.current) {
      addFileInputRef.current.click();
    }
  };
  const handleAddFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    setUploadingFile(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            const base64Payload = result.split(',')[1];
            resolve(base64Payload);
          };
          reader.onerror = () => reject(new Error('Failed to read file'));
          reader.readAsDataURL(file);
        });

        return createFile({
          name: file.name,
          fileBase64: base64
        });
      });

      await Promise.all(uploadPromises);
    } catch (error) {
      console.error('Error uploading files:', error);
      sendError(error as Error, {
        component: 'LocalFilesPanel',
        action: 'upload_files',
        fileCount: files.length
      });
    } finally {
      setUploadingFile(false);
      if (addFileInputRef.current) {
        addFileInputRef.current.value = '';
      }
    }
  };
  const isManagedFile = (file: UserFile | ManagedFile): file is ManagedFile => {
    return !('propelAuthUserID' in file);
  };
  const handleDeleteFile = (file: UserFile | ManagedFile) => {
    const managed = isManagedFile(file);
    if (managed && !canManageFiles) return;

    setFileToDelete({ file, managed });
    setShowDeleteDialog(true);
  };
  const handleDeleteConfirm = async () => {
    if (!fileToDelete) return;

    await deleteFile(fileToDelete.file.id, fileToDelete.managed);
    setShowDeleteDialog(false);
    setFileToDelete(null);
  };
  const handleDeleteCancel = () => {
    setShowDeleteDialog(false);
    setFileToDelete(null);
  };
  const handleDownloadFile = async (file: UserFile | ManagedFile) => {
    try {
      await downloadFile(dioptraFetch, file.fileUrl, file.name);
      analytics?.track("saved_file_downloaded", { fileId: file.id });
    } catch (error) {
      console.error('Error downloading file:', error);
      sendError(error as Error, {
        component: 'LocalFilesPanel',
        action: 'download_file',
        fileId: file.id,
        fileName: file.name
      });
      toastError('Failed to download file');
    }
  };
  const handleSelectFile = (file: UserFile | ManagedFile) => {
    if (onSelectSavedFile) {
      onSelectSavedFile(file.name, file.fileUrl);
    }
  };
  const isFileAttached = (fileName: string): boolean => {
    return selectedFiles.some(file => {
      const attachedFileName = "name" in file ? file.name : file.fileName;
      return normalizeFileName(attachedFileName) === normalizeFileName(fileName);
    });
  };
  const handleRemoveSavedFile = (fileName: string) => {
    if (onRemoveSavedFile) {
      onRemoveSavedFile(fileName);
    }
  };
  return { uploadingFile, showDeleteDialog, fileToDelete, addFileInputRef, handleAddFile, handleAddFileSelect, isManagedFile, handleDeleteFile, handleDeleteConfirm, handleDeleteCancel, handleDownloadFile, handleSelectFile, isFileAttached, handleRemoveSavedFile };
}
